import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ChargerService } from '../services/charger.service';
import * as chargerActions from './charger.actions';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';

import { MessageTranslateService } from '../../../shared/services/message.service';
import { ChargerFilterService } from '../services/charger-filter.service';
import {
  selectCurrentFilter,
  selectCurrentPagination,
} from './charger.selectors';
import { concatLatestFrom } from '@ngrx/operators';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import * as operatorsActions from '../../../store/operators/operators.actions';
import { ChargerRequestUtil } from '../utils/charger-request.util';

export class ChargerEffects {
  actions$: Actions = inject(Actions);
  chargerService: ChargerService = inject(ChargerService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);
  chargerFilterService = inject(ChargerFilterService);

  loadChargers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        chargerActions.loadChargerList,
        chargerActions.saveChargerSuccess,
        chargerActions.updateChargerFilterSuccess,
        chargerActions.changePaginationSuccessful,
        operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentFilter),
        this.store.select(selectCurrentPagination),
      ]),
      switchMap(([_action, selectedOperator, currentFilter, pagination]) =>
        this.chargerService
          .getChargers(
            ChargerRequestUtil.createChargerListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter
            )
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return chargerActions.loadChargerListSuccess({
                  chargersResponse: res.value!,
                });
              } else {
                return chargerActions.loadChargerListFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                chargerActions.loadChargerListFailure({
                  error: err.message || 'Loading chargers failed',
                })
              )
            )
          )
      )
    )
  );

  saveCharger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerActions.saveCharger),
      switchMap((requestData) =>
        this.chargerService.saveCharger(requestData.charger).pipe(
          map((res) => {
            if (res.isSuccess) {
              return chargerActions.saveChargerSuccess();
            } else {
              return chargerActions.saveChargerFailure({ error: res.error! });
            }
          }),
          catchError((err) =>
            of(
              chargerActions.saveChargerFailure({
                error: err.message || 'Saving charger failed',
              })
            )
          )
        )
      )
    )
  );

  showErrorForChargers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          chargerActions.loadChargerListFailure,
          chargerActions.saveChargerFailure
        ),
        tap((err) => this.messageService.error(err.error))
      ),
    { dispatch: false }
  );

  filterUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerActions.updateChargerFilter),
      map((_) => chargerActions.updateChargerFilterSuccess())
    )
  );

  changedPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerActions.changePagination),
      map((_) => chargerActions.changePaginationSuccessful())
    )
  );

  loadChargerNumbersByStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        chargerActions.loadChargerList,
        chargerActions.saveChargerSuccess,
        chargerActions.updateChargerFilterSuccess,
        chargerActions.changePaginationSuccessful,
        operatorsActions.SetCurrentOperatorSuccess,
        chargerActions.patchChargersStatuses
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentFilter),
        this.store.select(selectCurrentPagination),
      ]),
      switchMap(([_action, selectedOperator, currentFilter, pagination]) =>
        this.chargerService
          .getChargerNumbersByStatus(
            ChargerRequestUtil.createChargerListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter
            )
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return chargerActions.loadChargerNumbersByStatusSuccess({
                  value: res.value!,
                });
              } else {
                return chargerActions.loadChargerNumbersByStatusFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                chargerActions.loadChargerListFailure({
                  error: err.message || 'Loading charger numbers failed',
                })
              )
            )
          )
      )
    )
  );

  loadFilterParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerActions.loadChargerFilterParameters),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
      ]),
      switchMap(([_action, operator]) =>
        this.chargerService.getFilterParameres(operator!.id!).pipe(
          map((res) => {
            if (res.isSuccess) {
              return chargerActions.loadChargerFilterParametersSuccess({
                filterParameters: res.value!,
              });
            } else {
              return chargerActions.loadChargerFilterParametersFailure({
                error: res.error!,
              });
            }
          }),
          catchError((err) =>
            of(
              chargerActions.loadChargerFilterParametersFailure({
                error: err.message || 'Load location filter parameters failed',
              })
            )
          )
        )
      )
    )
  );
}
